import _ from "lodash";

import { Bar } from "app/charts/Bar";
import { BarChartTemplateV2 } from "app/components/widget/dashboard/BarChartTemplate-v2";
import { CHART_COLORS } from "app/constants/ChartColors";
import type {
    IPptBarChartRequest,
    IPptSeriesData,
    IPptSlideExportRequest,
} from "app/services/PptExportService/PptExportServiceTypes";

import { getBarChartWidgetPptOptions } from "../widget-utilities/widgetPpt/PptBarChartWidgetUtils";
import {
    augmentNameWithGAMark,
    getWidgetSubtitle,
    getWidgetTitle,
    getYAxisFormat,
} from "../widget-utilities/widgetPpt/PptWidgetUtils";

import { ChartWidget } from "./ChartWidget";

export class BarChartWidget extends ChartWidget {
    public isPptSupported = () => {
        return this.chartConfig.series && this.chartConfig.series.length > 0;
    };
    protected _highchartsConfig;
    protected _defaultDataFormat = "percent";

    public getDataForPpt = (): IPptSlideExportRequest => {
        return {
            title: getWidgetTitle(this),
            subtitle: getWidgetSubtitle(this, { showEntities: !this.isCompare() }),
            type: "bar",
            details: {
                format: getYAxisFormat(this),
                options: getBarChartWidgetPptOptions(),
                data: this.getSeriesForPowerpoint(),
            } as IPptBarChartRequest,
        };
    };

    private getSeriesForPowerpoint = (): IPptSeriesData[] => {
        return this.chartConfig.series.map(
            (
                series: {
                    name: string;
                    color: string;
                    data: Array<{ key: string; y: number }>;
                    isGAVerified: boolean;
                },
                index: number,
            ) => {
                const { name, data, isGAVerified } = series;
                const labels = data.map((point) => point.key);
                const values = data.map((point) => point.y / 100);
                const color = this.chartConfig.options.colors[index];
                return {
                    seriesName: augmentNameWithGAMark(name, isGAVerified),
                    seriesColor: color,
                    labels,
                    values,
                };
            },
        );
    };

    static getWidgetMetadataType() {
        return "BarChart";
    }

    static getWidgetResourceType() {
        return "BarChart";
    }

    get templateComponent() {
        return BarChartTemplateV2;
    }

    constructor() {
        super();
    }

    protected setMetadata() {
        const widgetProp = this._widgetConfig.properties;
        const chartOptions = {
            type: "BarChart",
            height: widgetProp.height,
            dashboardId: this.dashboardId,
        };
        this.metadata = { chartOptions };
    }

    callbackOnGetData(response: any) {
        const compareKeys = this._params.keys.split(",");
        this.metadata.chartOptions.isCompare = compareKeys.length > 1;
        this.metadata.chartOptions.viewOptions = this._viewOptions;
        this.metadata.chartOptions.linkParams = this.buildLinkParams();
        this.legendItems = this.getLegendItems(response.Data);
        void this.buildBar();
    }

    async buildBar(dataFormat?) {
        const _dataFormat = dataFormat ? dataFormat : this._defaultDataFormat;
        const formattedData = await this.getChartSeries(this.originalData);
        const bar = new Bar(this.metadata.chartOptions, formattedData, _dataFormat, this._params.webSource);
        this.chartConfig = bar.getChartConfig();
        this.applyLegendColorsToChartConfig(this.chartConfig);
    }

    /**
     * The legend colors each entry by its index in `legendItems`, while Highcharts colors each series
     * by its index in `options.colors`. Deriving a domain's color from two independent indexes is what
     * let the same domain be light blue in the legend and green in the graph tooltip (DRI-10792).
     * Resolve every compare series' color from the legend list by name instead, so the two cannot
     * drift apart even when the series order cannot be reconciled with the configured keys.
     */
    protected applyLegendColorsToChartConfig(chartConfig: any): void {
        const series = chartConfig?.series;
        // Single mode colors each *channel* individually, so there is no legend entry to align with.
        if (!Array.isArray(series) || series.length < 2 || !chartConfig.options) {
            return;
        }
        const legendColors = CHART_COLORS.compareMainColors;
        const colorByName = new Map<string, string>();
        (this.legendItems || []).forEach((item: any, index: number) => {
            const color = item?.color || item?.Color || legendColors[index];
            [item?.name, item?.id].forEach((alias) => {
                if (alias && !colorByName.has(String(alias))) {
                    colorByName.set(String(alias), color);
                }
            });
        });
        const resolvedColors = series.map((seriesItem: any) => colorByName.get(String(seriesItem?.name)));
        // Bail out rather than guess if any series has no matching legend entry.
        if (resolvedColors.some((color) => !color)) {
            return;
        }
        series.forEach((seriesItem: any, index: number) => {
            seriesItem.color = resolvedColors[index];
        });
        // Keep options.colors series-aligned: the PPT exporters read colors[seriesIndex].
        chartConfig.options.colors = resolvedColors;
    }

    /**
     * The API returns the compared entities in an arbitrary key order, while the widget legend is
     * rendered from the configured key order. Both the chart and the legend resolve their colors by
     * index, so the series have to follow the configured order for the two to stay in sync.
     * Returns null when the response cannot be ordered (single mode / keys not found in the response).
     */
    protected getOrderedCompareKeys(data: any): string[] | null {
        if (!_.isPlainObject(data)) {
            return null;
        }
        const compareKeys: string[] = String(this._params.keys || "")
            .split(",")
            .filter(Boolean);
        const orderedKeys = compareKeys.filter((key) => key in data);
        if (orderedKeys.length < 2) {
            return null;
        }
        return [...orderedKeys, ...Object.keys(data).filter((key) => !orderedKeys.includes(key))];
    }

    async getChartSeries(unorderedData, compareItemsKeys?: string[]): Promise<any[]> {
        const buildSeries = function (v: any, k: string) {
            return {
                data: _.merge(
                    {
                        Direct: 0,
                        Mail: 0,
                        OrganicSearch: 0,
                        DisplayAds: 0,
                        PaidSearch: 0,
                        Referrals: 0,
                        Social: 0,
                    },
                    v !== null
                        ? {
                              Direct: v["Direct"],
                              Mail: v["Mail"],
                              OrganicSearch: v["Organic Search"],
                              DisplayAds: v["Paid Referrals"],
                              PaidSearch: v["Paid Search"],
                              Referrals: v["Referrals"],
                              Social: v["Social"],
                          }
                        : {},
                ),
                name: k,
            };
        };
        const orderedKeys = this.getOrderedCompareKeys(unorderedData);
        return orderedKeys
            ? orderedKeys.map((key) => buildSeries(unorderedData[key], key))
            : _.map(unorderedData, buildSeries);
    }

    buildLinkParams() {
        const props = this.getProperties();
        return {
            country: props.country,
            duration: props.duration,
            isWWW: "*",
            key: props.key.map((k) => k.name).join(","),
        };
    }

    protected validateData(response: any) {
        const validByBase = super.validateData(response);
        const isEmpty = _.isEmpty(response);
        const emptyValues =
            isEmpty ||
            _.every(_.values(response), function (i) {
                return _.isNull(i) || _.isUndefined(i);
            });
        return validByBase && !emptyValues;
    }

    onResize() {
        if (this.chartConfig.series && this.metadata.chartOptions.isCompare) {
            if (this.pos.sizeX < 4) {
                this.chartConfig.options.plotOptions.column.pointWidth = 15;
                if (this.pos.sizeX === 2) {
                    this.chartConfig.options.xAxis.labels.style.fontSize = "9px";
                }
            } else {
                this.chartConfig.options.plotOptions.column.pointWidth = 20;
                this.chartConfig.options.xAxis.labels.style.fontSize = "13x";
            }
        } else {
            if (this.chartConfig.options) {
                if (this.pos.sizeX === 2) {
                    this.chartConfig.options.xAxis.labels.style.fontSize = "10px";
                } else {
                    this.chartConfig.options.xAxis.labels.style.fontSize = "14px";
                }
            }
        }
    }
}
BarChartWidget.register();
